import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/api";
import Seo from "@/components/store/Seo";
import ProductCard from "@/components/store/ProductCard";
import VideoSection from "@/components/store/VideoSection";
import FootwearLoader from "@/components/store/FootwearLoader";

const SORTS = [
  ["featured", "Featured"], ["best-selling", "Best Selling"], ["newest", "Newest"],
  ["price-asc", "Price: Low to High"], ["price-desc", "Price: High to Low"], ["discount", "Highest Discount"],
];

function FilterPanel({ facets, active, onChange, onClear }) {
  const groups = [
    { key: "size", label: "Size", options: facets.sizes || [] },
    { key: "color", label: "Color", options: facets.colors || [] },
    { key: "heel_type", label: "Heel Type", options: facets.heel_types || [] },
    { key: "occasion", label: "Occasion", options: facets.occasions || [] },
  ];
  return (
    <div className="space-y-7">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.2em]">Filters</p>
        <button onClick={onClear} data-testid="clear-filters-btn" className="text-xs text-[#C59B27] underline underline-offset-2">Clear all</button>
      </div>
      <div>
        <p className="tq-overline mb-3">Price</p>
        <div className="flex gap-2 items-center">
          <input type="number" placeholder="Min" value={active.min_price} data-testid="filter-min-price"
            onChange={(e) => onChange("min_price", e.target.value)} className="tq-input !py-2 w-1/2" />
          <span className="text-[#8E8E93]">—</span>
          <input type="number" placeholder="Max" value={active.max_price} data-testid="filter-max-price"
            onChange={(e) => onChange("max_price", e.target.value)} className="tq-input !py-2 w-1/2" />
        </div>
      </div>
      {groups.map((g) => g.options.length > 0 && (
        <div key={g.key}>
          <p className="tq-overline mb-3">{g.label}</p>
          <div className="flex flex-wrap gap-2">
            {g.options.map((o) => (
              <button key={o} onClick={() => onChange(g.key, active[g.key] === o ? "" : o)}
                data-testid={`filter-${g.key}-${String(o).toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-1.5 text-xs border transition-colors ${active[g.key] === o ? "bg-[#111111] text-[#FFFFFF] border-[#111111]" : "border-[#EAEAEA] hover:border-[#A8821B]"}`}>
                {o}
              </button>
            ))}
          </div>
        </div>
      ))}
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={active.in_stock === "true"} data-testid="filter-in-stock"
          onChange={(e) => onChange("in_stock", e.target.checked ? "true" : "")} className="accent-[#C59B27] w-4 h-4" />
        In stock only
      </label>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={active.discount === "true"} data-testid="filter-on-sale"
          onChange={(e) => onChange("discount", e.target.checked ? "true" : "")} className="accent-[#C59B27] w-4 h-4" />
        On sale
      </label>
    </div>
  );
}

export default function Collection() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [drawer, setDrawer] = useState(false);

  const active = useMemo(() => ({
    size: searchParams.get("size") || "", color: searchParams.get("color") || "",
    heel_type: searchParams.get("heel_type") || "", occasion: searchParams.get("occasion") || "",
    min_price: searchParams.get("min_price") || "", max_price: searchParams.get("max_price") || "",
    in_stock: searchParams.get("in_stock") || "", discount: searchParams.get("discount") || "",
    sort: searchParams.get("sort") || "featured", page: Number(searchParams.get("page") || 1),
  }), [searchParams]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };

  useEffect(() => {
    setMeta(null);
    api.get(`/taxonomy/${slug}`).then(({ data }) => setMeta(data)).catch(() => setMeta({ type: "none", data: { name: slug.replace(/-/g, " "), seo: {} } }));
  }, [slug]);

  useEffect(() => {
    setData(null);
    const params = { sort: active.sort, page: active.page, limit: 24 };
    if (meta?.type === "category") params.category = slug;
    else params.collection = slug;
    ["size", "color", "heel_type", "occasion", "min_price", "max_price", "in_stock", "discount"].forEach((k) => active[k] && (params[k] = active[k]));
    api.get("/products", { params }).then(({ data }) => setData(data)).catch(() => setData({ products: [], total: 0, pages: 1, facets: {} }));
  }, [slug, active, meta]);

  const activeChips = ["size", "color", "heel_type", "occasion", "min_price", "max_price", "in_stock", "discount"].filter((k) => active[k]);
  const title = meta?.data?.name || "Shop";

  return (
    <div data-testid="collection-page" className="tq-container py-8 sm:py-12">
      <Seo title={meta?.data?.seo?.title || title} description={meta?.data?.seo?.description || meta?.data?.description}
        schema={meta && {
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "/" },
            { "@type": "ListItem", position: 2, name: title },
          ],
        }} />
      <nav className="text-xs text-[#8E8E93] uppercase tracking-wider mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-[#A8821B]">Home</Link> <span className="mx-1.5">/</span> <span className="text-[#111111]">{title}</span>
      </nav>
      <div className="flex items-end justify-between gap-4 mb-2">
        <div>
          <h1 className="font-serif text-3xl sm:text-5xl">{title}</h1>
          {meta?.data?.description && <p className="text-sm text-[#666666] mt-2 max-w-xl">{meta.data.description}</p>}
        </div>
        <p className="text-xs text-[#8E8E93] shrink-0" data-testid="product-count">{data ? `${data.total} products` : ""}</p>
      </div>

      <div className="flex items-center justify-between gap-3 py-4 border-y border-[#EAEAEA] mt-6 sticky top-16 lg:top-20 bg-[#FFFFFF] z-20">
        <button onClick={() => setDrawer(true)} data-testid="mobile-filter-btn"
          className="lg:hidden flex items-center gap-2 text-xs font-semibold uppercase tracking-wider border border-[#111111] px-4 py-2.5">
          <SlidersHorizontal size={14} /> Filters
        </button>
        <div className="flex flex-wrap gap-2 flex-1 min-w-0">
          {activeChips.map((k) => (
            <button key={k} onClick={() => setParam(k, "")} data-testid={`chip-${k}`}
              className="flex items-center gap-1.5 text-[11px] bg-[#111111] text-[#FFFFFF] px-2.5 py-1.5">
              {k === "min_price" ? `₹${active[k]}+` : k === "max_price" ? `≤ ₹${active[k]}` : active[k] === "true" ? k.replace("_", " ") : active[k]}
              <X size={12} />
            </button>
          ))}
        </div>
        <select value={active.sort} onChange={(e) => setParam("sort", e.target.value)} data-testid="sort-select"
          className="border border-[#EAEAEA] bg-white text-xs px-3 py-2.5 outline-none focus:border-[#C59B27] uppercase tracking-wider shrink-0">
          {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-10 mt-8">
        <aside className="hidden lg:block">
          {data && <FilterPanel facets={data.facets} active={active} onChange={setParam} onClear={() => setSearchParams({})} />}
        </aside>
        <div>
          {!data ? (
            <FootwearLoader />
          ) : data.products.length === 0 ? (
            <div className="text-center py-20" data-testid="no-products-state">
              <p className="font-serif text-3xl mb-3">No pairs found</p>
              <p className="text-sm text-[#666666] mb-6">Try removing some filters or explore our new arrivals.</p>
              <Link to="/collections/new-arrivals" className="tq-btn-dark">Shop New Arrivals</Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
                {data.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
              {data.pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12" data-testid="pagination">
                  <button disabled={active.page <= 1} onClick={() => setParam("page", active.page - 1)} aria-label="Previous page" data-testid="page-prev"
                    className="w-10 h-10 border border-[#EAEAEA] flex items-center justify-center disabled:opacity-30 hover:border-[#C59B27]"><ChevronLeft size={16} /></button>
                  {[...Array(data.pages)].map((_, i) => (
                    <button key={i} onClick={() => setParam("page", i + 1)} data-testid={`page-${i + 1}`}
                      className={`w-10 h-10 border text-sm ${active.page === i + 1 ? "bg-[#111111] text-[#FFFFFF] border-[#111111]" : "border-[#EAEAEA] hover:border-[#C59B27]"}`}>
                      {i + 1}
                    </button>
                  ))}
                  <button disabled={active.page >= data.pages} onClick={() => setParam("page", active.page + 1)} aria-label="Next page" data-testid="page-next"
                    className="w-10 h-10 border border-[#EAEAEA] flex items-center justify-center disabled:opacity-30 hover:border-[#C59B27]"><ChevronRight size={16} /></button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <VideoSection placement="collection" title="Shop the Look" subtitle="Watch & Shop" />

      {meta?.data?.bottom_content && (
        <div className="mt-16 pt-10 border-t border-[#EAEAEA] max-w-3xl text-sm text-[#666666] leading-relaxed"
          dangerouslySetInnerHTML={{ __html: meta.data.bottom_content }} />
      )}

      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-[#111111]/60" onClick={() => setDrawer(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white max-h-[80vh] overflow-y-auto p-6 rounded-t-2xl" data-testid="mobile-filter-drawer">
            <div className="flex items-center justify-between mb-6">
              <p className="font-serif text-2xl">Filters</p>
              <button onClick={() => setDrawer(false)} aria-label="Close filters"><X size={22} /></button>
            </div>
            {data && <FilterPanel facets={data.facets} active={active} onChange={setParam} onClear={() => setSearchParams({})} />}
            <button onClick={() => setDrawer(false)} data-testid="apply-filters-btn" className="tq-btn-dark w-full mt-8">Show {data?.total ?? ""} Products</button>
          </div>
        </div>
      )}
    </div>
  );
}
